/**
 * Deterministic treatment-plan quality checks.
 *
 * Pure, reproducible rules over the parsed treatment-plan draft — no AI calls,
 * no async work. These produce warnings for provider review; they do not block
 * signing (required fields, date validity, and insurance diagnosis rules are
 * enforced separately at sign time).
 */

import type { QuestionnaireResponseItem } from '@medplum/fhirtypes';
import {
  parseTreatmentPlanDraft,
  validateTreatmentPlanDates,
  type ParsedTreatmentPlanGoal,
} from '@unify/fhir-model/resources/treatment-plan';
import { SYMPTOM_DISPLAY } from './treatment-plan/evidenceGroundedSymptoms';

export interface TreatmentPlanQualityFinding {
  /** Human-readable location, e.g. "Diagnosis 2 › Goal 1". */
  location: string;
  message: string;
}

export interface TreatmentPlanQualityCheck {
  id: string;
  label: string;
  status: 'passed' | 'warning';
  findings: TreatmentPlanQualityFinding[];
}

/**
 * A goal counts as measurable when it (or one of its objectives) contains a
 * quantitative aspect — any numeral, spelled-out count, or frequency word —
 * or names an explicit observation/tracking method.
 */
const MEASUREMENT_INDICATORS: RegExp[] = [
  // Any numeral: percentages, frequencies, durations, scores, ratios, deadlines.
  /\d/,
  // Spelled-out counts: "twice weekly", "identify two coping strategies"
  /\b(?:once|twice|thrice|one|two|three|four|five|six|seven|eight|nine|ten)\b/i,
  // Frequency words: "attend therapy weekly", "practice nightly"
  /\b(?:hourly|daily|nightly|weekly|monthly)\b/i,
  // Explicit observation/tracking method
  /\b(?:as measured by|as evidenced by|measured by|as reported (?:by|in)|self[- ]report|tracking log|mood log|journal entries|rating scale)\b/i,
  // Treatment-plan timeframe
  /\bby the (?:plan )?expiration\b/i,
];

function goalHasMeasurementIndicator(goal: ParsedTreatmentPlanGoal): boolean {
  const texts = [goal.text, ...goal.objectives.map((objective) => objective.text)]
    .filter((t): t is string => Boolean(t));
  return texts.some((text) => MEASUREMENT_INDICATORS.some((pattern) => pattern.test(text)));
}

export function runTreatmentPlanQualityChecks(
  items: QuestionnaireResponseItem[] | undefined,
): TreatmentPlanQualityCheck[] {
  const draft = parseTreatmentPlanDraft(items);

  const structural: TreatmentPlanQualityFinding[] = [];
  const measurability: TreatmentPlanQualityFinding[] = [];
  const planInfo: TreatmentPlanQualityFinding[] = [];

  if (!draft.treatmentFrequency) {
    planInfo.push({ location: 'Plan information', message: 'Treatment frequency is missing.' });
  }
  const dates = validateTreatmentPlanDates(items);
  for (const error of dates.errors) {
    planInfo.push({ location: 'Plan information', message: error });
  }

  draft.diagnoses.forEach((diagnosis, dIndex) => {
    const dLoc = `Diagnosis ${dIndex + 1}`;
    if (!diagnosis.code) {
      structural.push({ location: dLoc, message: 'No diagnosis code is documented.' });
    }
    if (!diagnosis.behavioralDefinitions.some((def) => def.text)) {
      structural.push({ location: dLoc, message: SYMPTOM_DISPLAY.missingQualityMessage });
    }
    if (diagnosis.goals.length === 0) {
      structural.push({ location: dLoc, message: 'No goal is documented.' });
    }

    diagnosis.goals.forEach((goal, gIndex) => {
      const gLoc = `${dLoc} › Goal ${gIndex + 1}`;
      if (!goal.text) {
        structural.push({ location: gLoc, message: 'Goal text is empty.' });
      }
      if (goal.objectives.length === 0) {
        structural.push({ location: gLoc, message: 'Goal has no objective.' });
      }
      goal.objectives.forEach((objective, oIndex) => {
        const oLoc = `${gLoc} › Objective ${oIndex + 1}`;
        if (!objective.text) {
          structural.push({ location: oLoc, message: 'Objective text is empty.' });
        }
        if (!objective.interventions) {
          structural.push({ location: oLoc, message: 'Objective has no intervention.' });
        }
      });
      if (goal.text && !goalHasMeasurementIndicator(goal)) {
        measurability.push({
          location: gLoc,
          message: 'This goal may not include an explicit measurement criterion.',
        });
      }
    });
  });

  return [
    {
      id: 'plan-information',
      label: 'Plan information',
      status: planInfo.length > 0 ? 'warning' : 'passed',
      findings: planInfo,
    },
    {
      id: 'plan-structure',
      label: 'Plan structure',
      status: structural.length > 0 ? 'warning' : 'passed',
      findings: structural,
    },
    {
      id: 'goal-measurability',
      label: 'Goal measurability',
      status: measurability.length > 0 ? 'warning' : 'passed',
      findings: measurability,
    },
  ];
}
